import { CONTROL_LIT } from "@majourneys/shared/ui";
import { useTranslation } from "react-i18next";

import { Icon } from "@majourneys/icons";
import { panelStyle } from "../../../shared/ui/card-frame";

/**
 * "Where to go in {city}" + the best-time card, in pewter.
 *
 * The neighborhood pills stay plain white controls with one navy hairline —
 * owner ruling 2026-09-05: the pewter band is for surfaces you read, never for
 * a chip. The best-time card keeps its warm tint, now as the panel's own fill
 * rather than a glass gradient over the page.
 */

const NAVY = "#110E39";
const MUTED = "#5a5d77";
/** A control at rest — the one definition, in `@majourneys/shared/ui`. */
const CONTROL_FILL = CONTROL_LIT;
const CONTROL_EDGE = "rgba(17,14,57,0.16)";
/** The same sheet tone as `panelStyle`'s default, warmed toward the gold end. */
const WARM_FILL =
  "linear-gradient(180deg, rgba(253,251,248,1) 0%, rgba(251,245,236,1) 60%, rgba(249,240,228,1) 100%)";

interface CategoryNeighborhoodsProps {
  cityName: string | null;
  neighborhoods: string[];
  bestTime: { window: string; note: string } | null;
}

export function CategoryNeighborhoods({ cityName, neighborhoods, bestTime }: CategoryNeighborhoodsProps) {
  const { t } = useTranslation();
  if (!cityName || (neighborhoods.length === 0 && !bestTime)) return null;

  return (
    <section aria-label={t("activity.neighborhoods_aria", { city: cityName })} className="py-10">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,360px)]">
        {neighborhoods.length > 0 && (
          <div className="p-6" style={panelStyle(22)}>
            <h2
              className="text-[20px] font-semibold leading-tight sm:text-[22px]"
              style={{ color: NAVY, fontFamily: "var(--font-geist)", letterSpacing: "-0.02em" }}
            >
              {t("activity.where_to_go", { defaultValue: "Where to go in {{city}}", city: cityName })}
            </h2>
            <p className="mt-2 text-[14px]" style={{ color: MUTED }}>
              {t("activity.pick_your_base", { defaultValue: "Pick your base, each neighborhood has its own vibe." })}
            </p>
            <ul className="mt-4 flex flex-wrap gap-2">
              {neighborhoods.map((n) => (
                <li
                  key={n}
                  className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-semibold"
                  style={{ color: NAVY, background: CONTROL_FILL, boxShadow: `0 0 0 1.5px ${CONTROL_EDGE}` }}
                >
                  <Icon name="ri-map-pin-2-line" className="text-[14px] text-[color:var(--color-primary)]" aria-hidden="true" />
                  {n}
                </li>
              ))}
            </ul>
          </div>
        )}

        {bestTime && (
          <div className="p-6" style={panelStyle(22, WARM_FILL)}>
            <div className="flex items-center gap-3">
              <span
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl text-[22px] text-[color:var(--color-primary)]"
                style={{ background: CONTROL_FILL, boxShadow: `0 0 0 1.5px ${CONTROL_EDGE}` }}
              >
                <Icon name="ri-calendar-check-line" aria-hidden="true" />
              </span>
              <div>
                <p className="text-[11px] font-bold uppercase" style={{ color: MUTED, letterSpacing: "0.09em" }}>
                  {t("activity.best_time", { defaultValue: "Best time" })}
                </p>
                <p className="text-[16px] font-semibold leading-tight" style={{ color: NAVY }}>
                  {bestTime.window}
                </p>
              </div>
            </div>
            <p className="mt-3 text-[13.5px] leading-relaxed" style={{ color: MUTED }}>
              {bestTime.note}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
